import { parseDate, today } from "@internationalized/date";
import { useMemo, useState } from "react";
import { Button as AriaButton } from "react-aria-components";

import closeIcon from "@/assets/figma/store-overview/close.svg";
import newChatIcon from "@/assets/figma/store-overview/new-chat.svg";
import reportIcon from "@/assets/figma/store-overview/report.svg";
import {
  StoreOverviewCompositionChart,
  StoreOverviewTrendChart,
} from "@/features/store-overview/StoreOverviewCharts";
import {
  formatComparisonDelta,
  formatShareBps,
  formatStoreOverviewAmount,
  formatStoreOverviewAsOf,
  formatStoreOverviewKpi,
  isValidCustomRange,
  MAX_CUSTOM_RANGE_DAYS,
  storeOverviewMissingReason,
} from "@/features/store-overview/storeOverviewPresentation";
import type { StoreOverview } from "@/features/store-overview/storeOverviewSchemas";
import type { StoreOverviewRangeKind } from "@/features/desktop-shell/desktopShellData";
import { MerryDatePicker, type MerryDateRange } from "@/shared/ui/MerryDatePicker";

interface StoreOverviewPanelProps {
  customEnd: string;
  customStart: string;
  isCreatingChat: boolean;
  isCreatingReport: boolean;
  isTemporaryChatCollapsed: boolean;
  onClose: () => void;
  onCreateReport: () => void;
  onOpenTemporaryChat: () => void;
  onRangeChange: (
    range: StoreOverviewRangeKind,
    customStart?: string,
    customEnd?: string,
  ) => void;
  overview: StoreOverview;
  range: StoreOverviewRangeKind;
  reportNotice: string;
}

const rangeLabels: ReadonlyArray<{
  key: StoreOverviewRangeKind;
  label: string;
}> = [
  { key: "today", label: "今天" },
  { key: "7d", label: "7天" },
  { key: "30d", label: "30天" },
  { key: "custom", label: "自定义" },
];

function safeDate(value: string) {
  try {
    return value ? parseDate(value) : null;
  } catch {
    return null;
  }
}

export function StoreOverviewPanel({
  customEnd,
  customStart,
  isCreatingChat,
  isCreatingReport,
  isTemporaryChatCollapsed,
  onClose,
  onCreateReport,
  onOpenTemporaryChat,
  onRangeChange,
  overview,
  range,
  reportNotice,
}: StoreOverviewPanelProps) {
  const defaultEnd = useMemo(
    () => today(overview.queryScope.reportingTimezone),
    [overview.queryScope.reportingTimezone],
  );
  const defaultStart = useMemo(
    () => defaultEnd.subtract({ days: 6 }),
    [defaultEnd],
  );
  const canonicalRange = useMemo<MerryDateRange>(
    () => ({
      end: safeDate(customEnd) ?? defaultEnd,
      start: safeDate(customStart) ?? defaultStart,
    }),
    [customEnd, customStart, defaultEnd, defaultStart],
  );
  const [rangeError, setRangeError] = useState("");

  const isEmpty = overview.coverage.state === "empty";
  const isPartial = overview.coverage.state === "partial";
  const isStale = overview.coverage.reasonCodes.some(
    (reason) => reason === "source_stale" || reason === "source_snapshot_stale",
  );
  const trendSeries = overview.series.filter((item) =>
    item.key === "gross_sales_minor" || item.key === "ad_spend_minor",
  );

  const updateCustomRange = (nextRange: MerryDateRange) => {
    const start = nextRange.start.toString();
    const end = nextRange.end.toString();
    if (!isValidCustomRange(start, end)) {
      setRangeError(`自定义范围最多 ${MAX_CUSTOM_RANGE_DAYS} 个自然日`);
      return;
    }
    setRangeError("");
    onRangeChange("custom", start, end);
  };

  return (
    <section
      aria-labelledby="store-overview-title"
      className="store-overview-panel"
      data-coverage-state={overview.coverage.state}
      data-figma-node-id="736:110402"
      data-testid="store-overview-panel"
    >
      <header className="store-overview-panel__header">
        <div>
          <h1 id="store-overview-title" tabIndex={-1}>全店数据总览</h1>
          <p>数据截至 {formatStoreOverviewAsOf(overview)}</p>
        </div>
        <nav aria-label="总览时间范围" className="store-overview-panel__ranges">
          {rangeLabels.map((item) => (
            <AriaButton
              aria-pressed={range === item.key}
              className="store-overview-range-button"
              data-active={range === item.key || undefined}
              key={item.key}
              onPress={() =>
                item.key === "custom"
                  ? onRangeChange(
                      "custom",
                      canonicalRange.start.toString(),
                      canonicalRange.end.toString(),
                    )
                  : onRangeChange(item.key)
              }
            >
              {item.label}
            </AriaButton>
          ))}
        </nav>
        <div className="store-overview-panel__actions">
          <AriaButton
            aria-label="存为报告"
            className="store-overview-action-button"
            isDisabled={!overview.actions.canExportReport || isCreatingReport || isEmpty}
            onPress={onCreateReport}
          >
            <img alt="" src={reportIcon} />
            <span>{isCreatingReport ? "创建中" : "存为报告"}</span>
          </AriaButton>
          <AriaButton
            aria-label={isTemporaryChatCollapsed ? "展开临时聊天" : "打开临时聊天"}
            className="store-overview-action-button store-overview-action-button--primary"
            isDisabled={
              !overview.actions.canCreateTemporaryConversation ||
              isCreatingChat ||
              isEmpty
            }
            onPress={onOpenTemporaryChat}
          >
            <img alt="" src={newChatIcon} />
            <span>
              {isCreatingChat
                ? "创建中"
                : isTemporaryChatCollapsed
                  ? "展开聊天"
                  : "临时聊天"}
            </span>
          </AriaButton>
          <AriaButton
            aria-label="关闭全店数据总览"
            className="store-overview-icon-button"
            onPress={onClose}
          >
            <img alt="" src={closeIcon} />
          </AriaButton>
        </div>
      </header>

      {range === "custom" ? (
        <div className="store-overview-panel__custom-range">
          <MerryDatePicker
            aria-label="选择全店总览日期范围"
            maxValue={today(overview.queryScope.reportingTimezone)}
            mode="range"
            onChange={updateCustomRange}
            timeZone={overview.queryScope.reportingTimezone}
            value={canonicalRange}
          />
          <div>
            <strong>自定义范围</strong>
            <p>{canonicalRange.start.toString()} 至 {canonicalRange.end.toString()}</p>
            <p>服务端允许含首尾最多 {MAX_CUSTOM_RANGE_DAYS} 个自然日。</p>
            {rangeError ? <p role="alert">{rangeError}</p> : null}
          </div>
        </div>
      ) : null}

      {isPartial || isStale ? (
        <aside className="store-overview-coverage" role="status">
          <strong>{isStale ? "部分数据已过期" : "部分数据尚未覆盖"}</strong>
          <span>
            已覆盖 {overview.coverage.sources.covered}/{overview.coverage.sources.expected} 个来源；未知值不会按 0 计算。
          </span>
          {overview.coverage.reasonCodes.length > 0 ? (
            <small>{overview.coverage.reasonCodes.map(storeOverviewMissingReason).join(" · ")}</small>
          ) : null}
        </aside>
      ) : null}

      {reportNotice ? <p className="store-overview-report-notice" role="status">{reportNotice}</p> : null}

      {isEmpty ? (
        <div className="store-overview-empty" role="status">
          <strong>暂无可验证的全店经营快照</strong>
          <p>绑定店铺并等待服务端采集完成后，这里会展示数据；当前不会用 0 代替未知值。</p>
        </div>
      ) : (
        <div className="store-overview-panel__content">
          <section className="store-overview-headline">
            <div>
              <span>经营摘要</span>
              <h2>{overview.headline.title}</h2>
              <p>{overview.headline.summary}</p>
            </div>
            <dl>
              <div><dt>覆盖来源</dt><dd>{overview.coverage.sources.covered}/{overview.coverage.sources.expected}</dd></div>
              <div><dt>快照</dt><dd>{overview.snapshotId.slice(0, 12)}</dd></div>
            </dl>
          </section>

          {overview.alerts.length > 0 ? (
            <section aria-label="全店预警" className="store-overview-alerts">
              {overview.alerts.map((alert) => (
                <p data-severity={alert.severity} key={alert.id}>{alert.title}</p>
              ))}
            </section>
          ) : null}

          <section aria-label="关键经营指标" className="store-overview-kpis">
            {overview.kpis.map((kpi) => {
              const delta = formatComparisonDelta(kpi.comparisonDeltaBps);
              return (
                <article key={kpi.key}>
                  <span>{kpi.label}</span>
                  <strong>{formatStoreOverviewKpi(kpi)}</strong>
                  {kpi.value === null ? (
                    <small>{storeOverviewMissingReason(kpi.missingReasonCode || "not_collected")}</small>
                  ) : delta ? (
                    <small data-positive={delta.startsWith("+") || undefined}>较上一周期 {delta}</small>
                  ) : (
                    <small>暂无同周期对比</small>
                  )}
                </article>
              );
            })}
          </section>

          <div className="store-overview-panel__grid">
            <section className="store-overview-card store-overview-card--ranking">
              <header><h2>店铺贡献排名</h2><span>同一快照</span></header>
              {overview.rankings.stores.length === 0 ? (
                <p className="store-overview-empty-copy">当前范围暂无可排名店铺。</p>
              ) : (
                <ol>
                  {overview.rankings.stores.map((store, index) => (
                    <li key={store.resourceId}>
                      <span>{index + 1}</span>
                      <div><strong>{store.label}</strong><small>{store.provider || "其他"} · {store.market || "未知市场"}</small></div>
                      <div><strong>{formatStoreOverviewAmount(store.value, store.currency)}</strong><small>{formatShareBps(store.shareBps)}</small></div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="store-overview-card store-overview-card--attention">
              <header><h2>需关注店铺 Top 3</h2><span>{overview.attention.length} 条</span></header>
              {overview.attention.length === 0 ? (
                <p className="store-overview-empty-copy">当前没有需立即关注的店铺。</p>
              ) : (
                <ol>
                  {overview.attention.map((item) => (
                    <li data-severity={item.severity} key={item.id}>
                      <i aria-hidden="true" />
                      <div><strong>{item.title}</strong><small>{item.provider || "经营数据"}</small></div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="store-overview-card store-overview-card--trend">
              <header><h2>经营趋势</h2><span>销售额 / 广告消耗</span></header>
              <StoreOverviewTrendChart series={trendSeries} />
            </section>

            <section className="store-overview-card store-overview-card--composition">
              <header><h2>销售结构</h2><span>平台 / 市场</span></header>
              <div>
                <section><h3>平台</h3><StoreOverviewCompositionChart items={overview.composition.platform} label="平台" /></section>
                <section><h3>市场</h3><StoreOverviewCompositionChart items={overview.composition.market} label="市场" /></section>
              </div>
            </section>
          </div>
        </div>
      )}
    </section>
  );
}
